import React from 'react';
import { FiUsers, FiShield, FiTag, FiActivity } from 'react-icons/fi';
import { ProjectCollaboratorsCard } from '../ProjectCollaboratorsCard';

export const ROLE_BADGE_STYLE: Record<string, string> = {
  'Project Owner': 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  Owner: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
  Manager: 'bg-purple-500/10 border-purple-500/20 text-purple-400',
  Editor: 'bg-emerald-500/10 border-emerald-500/20 text-emerald-400',
  Guest: 'bg-zinc-800 border-zinc-700 text-zinc-400',
};

interface ProjectRolesTabProps {
  project: any;
  projectCrew: any[];
  projectActivities: any[];
  user: any;
}

export const ProjectRolesTab: React.FC<ProjectRolesTabProps> = ({
  project,
  projectCrew,
  projectActivities,
  user,
}) => {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
      {/* Left Column (3/5) */}
      <div className="lg:col-span-3 space-y-6">
        {/* Core statistics cards */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 flex items-center justify-between">
            <div>
              <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">
                Members
              </p>
              <p className="text-white text-xl font-bold mt-1.5">{projectCrew.length}</p>
            </div>
            <div className="p-2.5 rounded-xl bg-blue-500/10 text-blue-400">
              <FiUsers className="w-5 h-5" />
            </div>
          </div>
          <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 flex items-center justify-between">
            <div>
              <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">Roles</p>
              <p className="text-white text-xl font-bold mt-1.5">4</p>
            </div>
            <div className="p-2.5 rounded-xl bg-purple-500/10 text-purple-400">
              <FiShield className="w-5 h-5" />
            </div>
          </div>
          <div className="bg-[#0f0f0f] border border-white/5 rounded-2xl p-4 flex items-center justify-between">
            <div>
              <p className="text-zinc-500 text-[10px] font-bold uppercase tracking-wider">
                Pending
              </p>
              <p className="text-white text-xl font-bold mt-1.5">
                {projectCrew.filter((cm) => cm.status === 'Pending').length}
              </p>
            </div>
            <div className="p-2.5 rounded-xl bg-amber-500/10 text-amber-400">
              <FiTag className="w-5 h-5" />
            </div>
          </div>
        </div>

        {/* Team Members & Pending Invitations */}
        <ProjectCollaboratorsCard
          projectUuid={project.id}
          currentUserId={user?.id}
          isOwner={project.isMyProject}
          permissions={project.permissions}
          isArchived={project.isArchived}
        />
      </div>

      {/* Right Column (2/5) */}
      <div className="lg:col-span-2 space-y-6">
        {/* Roles Activity Feed */}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-3xl p-6">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-5">
            Roles Activity
          </h3>
          <div className="space-y-4">
            {projectActivities.map((act) => (
              <div key={act.id} className="flex gap-3">
                <div className="w-7 h-7 rounded-full bg-blue-500/10 text-blue-400 flex items-center justify-center shrink-0 mt-0.5">
                  <FiActivity className="w-3.5 h-3.5" />
                </div>
                <div>
                  <p className="text-zinc-300 text-xs font-medium">
                    <span className="text-white font-bold">{act.user}</span> {act.action}
                  </p>
                  <span className="text-zinc-550 text-[9px] block mt-0.5">{act.timestamp}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Role Permissions Mappings */}
        <div className="bg-[#0f0f0f] border border-white/5 rounded-3xl p-6">
          <h3 className="text-sm font-bold text-white uppercase tracking-wider mb-4">
            Role Permissions
          </h3>
          <div className="space-y-3.5">
            {[
              {
                role: 'Owner',
                badge: ROLE_BADGE_STYLE['Project Owner'],
                access: 'Full access',
              },
              {
                role: 'Manager',
                badge: ROLE_BADGE_STYLE.Manager,
                access: 'Edit + invite',
              },
              {
                role: 'Editor',
                badge: ROLE_BADGE_STYLE.Editor,
                access: 'Edit access',
              },
              {
                role: 'Guest',
                badge: ROLE_BADGE_STYLE.Guest,
                access: 'View only',
              },
            ].map((perm) => (
              <div
                key={perm.role}
                className="flex items-center justify-between text-xs font-semibold"
              >
                <span
                  className={`px-2 py-0.5 rounded-full border text-[8px] font-extrabold uppercase ${perm.badge}`}
                >
                  {perm.role}
                </span>
                <span className="text-zinc-450">{perm.access}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
